import { TemplateRef } from '@angular/core';
import { IBixiTimeProps } from '@bixi/core/time';
import { NzSafeAny } from 'ng-zorro-antd/core/types';
import { Observable } from 'rxjs';
import { IPagination } from './pagination-list.base';

export type IFn<T> = (val: NzSafeAny, row: IRow, index: number) => T;
export type IFnNoVal<T> = (row: IRow, index: number) => T;
export type IFnPure<T> = () => T;
export type IAllowFn<T> = IFn<T> | T;
export type IAllowFnPure<T> = IFnPure<T> | T;
export type IAllowFnNoVal<T> = IFnNoVal<T> | T;
export type IRenderProp = string | number | boolean | undefined;

export enum EColType {
  'checkbox' = 'checkbox',
  'text' = 'text',
  'link' = 'link',
  'time' = 'time',
  'status' = 'status',
  'progress' = 'progress',
  'template' = 'template',
  'component' = 'component',
  'operations' = 'operations'
}

interface IBaseCol {
  name?: string;
  nameRef?: string; // 自定义表头
  key: string; // key 必须存在，operations 和 checkable 都需要
  keyAlias?: string; // key 别名，如果 key 重复时使用该属性
  visible?: IAllowFnPure<boolean>;
  titleTipIcon?: string;
  titleTipContent?: string | TemplateRef<void>;
  width?: string;
  maxWidth?: string;
  align?: 'left' | 'center' | 'right';
  value?: IAllowFn<IRenderProp>;
  onClick?: (row: NzSafeAny, index: number) => void;
  fixed?: 'left' | 'right';
  sort?: {
    key?: string;
    direction?: 'descend' | 'ascend' | null;
  };
  filter?: {
    key?: string;
    multiple?: boolean;
    options:
    | {
      text: string;
      value: NzSafeAny;
      byDefault?: boolean;
    }[]
    | Observable<
      {
        text: string;
        value: NzSafeAny;
        byDefault?: boolean;
      }[]
    >;
  };
}

export interface IFilterParams {
  [key: string]: NzSafeAny;
}

export interface ISearchParams {
  filterParams: IFilterParams;
  pagination: IPagination;
}

// 文字
export interface ITextCol extends IBaseCol {
  type?: EColType.text;
  ellipsis?: boolean;
  tooltip?: IAllowFn<IRenderProp>;
}

// 链接
export interface ILinkCol extends IBaseCol {
  type: EColType.link;
  ellipsis?: boolean;
  tooltip?: IAllowFn<IRenderProp>;
  href?: IAllowFn<IRenderProp>;
  target?: '_blank' | '_self' | '_parent' | '_top';
}

// 时间
export interface ITimeCol extends IBaseCol, IBixiTimeProps {
  type: EColType.time;
}

export enum EProgressStatus {
  'stop' = 'stop',
  'processing' = 'processing',
  'success' = 'success',
  'failed' = 'failed',
  'starting' = 'starting'
}

// 进度条
export interface IProgressCol extends IBaseCol {
  type: EColType.progress;
  status: IAllowFn<EProgressStatus>;
  tooltip?: IAllowFn<IRenderProp>;
}

// 状态
export interface IStatus {
  text?: string;
  value: string | number;
  type: string | EStatus;
}

// 状态类型枚举
export enum EStatus {
  'success' = 'success',
  'error' = 'error',
  'default' = 'default',
  'processing' = 'processing',
  'stop' = 'stop'
}

export interface IStatusCol extends IBaseCol {
  type: EColType.status;
  options?: IStatus[];
  tooltip?: IAllowFn<IRenderProp>;
}

// 模板
export interface ITemplateCol extends IBaseCol {
  type: EColType.template;
  ref: string;
}

// checkbox
export interface ICheckboxCol extends IBaseCol {
  type: EColType.checkbox;
  selectable?: IAllowFn<boolean>; // 是否可选择
  name?: string;
  selectionList?: {
    // dropdown
    text: string;
    onSelect(...args: NzSafeAny[]): NzSafeAny;
  }[];
  onSelected?: (row: IRow, index: number, checked: boolean) => void;
}

// 操作栏
export interface IOperationsCol extends IBaseCol {
  type: EColType.operations;
  disabled?: IFnNoVal<boolean>;
  operations: (row: IRow, index: number) => IOperationsColBtn[];
}

export interface IOperationsColBtn {
  name: string;
  disabled?: IAllowFnNoVal<boolean>;
  visible?: IAllowFnNoVal<boolean>;
  tooltip?: IAllowFnNoVal<NzSafeAny>;
  onClick?: IFnNoVal<NzSafeAny>;
  children?: IOperationsColBtn[];
}

export enum EBixiCustomTplType {
  header = 'header',
  body = 'body'
}

export interface IBixiTableStatusConfigCustom {
  text: string;
  className?: string;
  style?: Partial<CSSStyleDeclaration>;
}

export interface IBixiTableStatusConfig {
  [prop: string]: EStatus | IBixiTableStatusConfigCustom;
}

export interface IBixiTableConfig {
  status: IBixiTableStatusConfig;
}

export type ICol = ITextCol | ILinkCol | ITimeCol | IStatusCol | IProgressCol | ITemplateCol | IOperationsCol | ICheckboxCol;

export type ICols = ICol[];

export type IRow = NzSafeAny;

export type IRows = IRow[];

export type IRowKey = string | number;

export type IInternalColFixed = {
  _left?: string | false;
  _right?: string | false;
  _leftLastFixed?: boolean; // 是否是最后一个 left fixed
  _rightFirstFixed?: boolean; // 是否是第一个 right fixed
};

export type IInternalCol = ICol & IInternalColFixed;

export type ITableLayout = 'fixed' | 'auto';

export type IInternalCols = IInternalCol[];
